// Halftone filter — converts brightness into circular dots or lines on a grid.

import type { LayerSpec } from '@/core/pipeline';
import { parseHex, type RGB } from '@/lib/color';

type HalftoneParams = {
  shape: 'dot' | 'line';
  cell: number;     // grid size in pixels
  ink: string;      // foreground hex
  paper: string;    // background hex
  invert: boolean;
};

export const halftoneSpec: LayerSpec = {
  kind: 'halftone',
  category: 'filter',
  animated: false,
  label: 'Halftone',
  defaults: { shape: 'dot', cell: 8, ink: '#0a0a0a', paper: '#ffffff', invert: false } satisfies HalftoneParams,
  schema: {
    shape:  { type: 'select', label: 'Shape',  options: ['dot', 'line'], default: 'dot' },
    cell:   { type: 'int',    label: 'Cell',   min: 2, max: 64, step: 1, default: 8 },
    ink:    { type: 'color',  label: 'Ink',    default: '#0a0a0a' },
    paper:  { type: 'color',  label: 'Paper',  default: '#ffffff' },
    invert: { type: 'bool',   label: 'Invert', default: false },
  },
  run: (input, params) => {
    if (!input) return new ImageData(1, 1);
    const p = params as HalftoneParams;
    const ink = parseHex(p.ink) ?? ([0, 0, 0] as RGB);
    const paper = parseHex(p.paper) ?? ([255, 255, 255] as RGB);
    const w = input.width, h = input.height;
    const cell = Math.max(2, Math.floor(p.cell));
    const out = new ImageData(w, h);
    const dst = out.data;
    const src = input.data;

    for (let cy = 0; cy < h; cy += cell) {
      for (let cx = 0; cx < w; cx += cell) {
        // Average luminance of cell
        let sum = 0, n = 0;
        const x1 = Math.min(w, cx + cell);
        const y1 = Math.min(h, cy + cell);
        for (let y = cy; y < y1; y++) {
          for (let x = cx; x < x1; x++) {
            const i = (y * w + x) * 4;
            sum += 0.2126 * (src[i] as number) + 0.7152 * (src[i + 1] as number) + 0.0722 * (src[i + 2] as number);
            n++;
          }
        }
        const avg = n === 0 ? 0 : sum / (n * 255);
        const t = p.invert ? avg : 1 - avg;
        const half = cell / 2;
        const r2max = half * half;

        for (let y = cy; y < y1; y++) {
          for (let x = cx; x < x1; x++) {
            const i = (y * w + x) * 4;
            let on: boolean;
            if (p.shape === 'dot') {
              const dx = x - cx - half + 0.5;
              const dy = y - cy - half + 0.5;
              const d2 = dx * dx + dy * dy;
              on = d2 <= r2max * t;
            } else {
              const dy = y - cy - half + 0.5;
              on = Math.abs(dy) <= half * t;
            }
            const c = on ? ink : paper;
            dst[i] = c[0]; dst[i + 1] = c[1]; dst[i + 2] = c[2];
            dst[i + 3] = src[i + 3] as number;
          }
        }
      }
    }
    return out;
  },
};
